<script setup lang="ts">

import CardProcessingDialog from "@/components/dialogs/CardProcessingDialog.vue";
import PreventBenefitsDialog from "@/components/dialogs/PreventBenefitsDialog.vue";
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { usePosSetupStore } from "@/store/posSetupStore";
import { useBooking as useBookingStore } from "@/store/useBooking";
import { useCalendarStore } from "@/store/useCalendarStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { useUserModeChange } from "@/store/useUserModeChange";
import { getBrowserInfo } from "@/utils/BrowserInfo";
import { Round2String, getCardProviders } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { isMembershipCoupon, isMembershipCouponBlockedForUser } from "@/utils/couponRules";
import { mergeDockets } from "@/utils/useDocket";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import {
  creditLimitValidator,
  customRegexValidator,
  decimalRule,
  minMaxValidator,
  minValidator,
  requiredValidator,
} from "@validators";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import AddToMyTabDialog from "../dialogs/AddToMyTabDialog.vue";
import type { Emit, Props, Tab } from "./types";

// props
const props = defineProps<Props>();

// emit
const emit = defineEmits<Emit>();

// store
const usePayment = usePaymentStore();
const discount = useDiscountCode();
const userStore = useUserStore();
const bookingStore = useBookingStore();
const locationStore = useLocationStore();
const calenderStore = useCalendarStore();
const roleStore = useUserRole();
const modeStore = useUserModeChange();
const globalStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(globalStore);
const { isUserMode } = storeToRefs(modeStore);
const posSetupStore = usePosSetupStore();

const {
  getCouponList,
  payWithGiftVoucherOrCoupon,
  initiateDocketForBooking,
  handleUserCreditAndWaiveFeePayment,
  createEditDocketForCustomizePurchase,
  payByUserCredit,
  payByGiftVoucher,
  payByPos,
  eventDuePayByPos,
  eventDuePayByWaiveFee,
  initiateDocketForEvent,
  processBooking
} = usePayment;

const { handleDiscountCodeApply, validateBookingDateAppliedDiscountCode } = discount;
const { apps } = storeToRefs(roleStore);

const { couponList, docketId, docketLineId, lowBalanceError } = storeToRefs(usePayment);

const { addCardDialogMessage, isProcessingCardDialogVisible } = storeToRefs(bookingStore);

const { subscribeBookingPayment } = bookingStore;

const { currentLocationDetails, currentLocation, userId } = storeToRefs(userStore);

const { locationList } = storeToRefs(locationStore);
const { usersList } = storeToRefs(calenderStore);
const { getUserList } = calenderStore;


// Data
const showPreventBenefits = ref(false)
const bookingOwnerUser = ref<any>(null)
const usersForCouponsList = ref([])
const barcodeReaderID = ref('')
const router = useRouter()
const docketBookingId = ref('')
const docketTotalDue = ref(0)
const isAddToMyTabDialogVisible = ref(false)
const quantity = ref<any>("");
const selectedCoupon = ref<any>();
const giftVoucherId = ref<string>("");
const appliedGiftVoucherDetails = ref<any>("");
const isInvalidCoupon = ref<boolean>(false);
const amountToRedeem = ref<number>();
const hasGiftVoucherRecords = ref(false);
const feeWaiveReason = ref<string>("");
const isTooltipVisible = ref<boolean>(false);
const selectedCreditCard = ref<any>('newCard');
const saveNewCard = ref<boolean>(false);
const payingDocket = ref<string | undefined>();
const canPayAtPos = ref(false)
const couponUser = ref(props?.bookingProp?.users?.[0] || props?.owner || '')
const accountType = ref('account');
const processingPayment = ref(false)
// Form Refs
const refVFormCoupon = ref<any>("");
const refVFormGiftVoucher = ref<any>("");
const refVFormWaive = ref<any>();
const refVFormCreditCard = ref<any>();
const refCreditAmtForm = ref<any>();

// Methods
const getUsersForCouponsList = computed(() => {
  let items:any = []
  usersForCouponsList.value.forEach((u:any) => {
    let title = (u.firstName + u.lastName).trim() != '' ? u.firstName + ' ' + u.lastName : u.username
    items.push({value:u.id,title, props: {subtitle: u.username}})
  })

  return items
})

const doBarcodeReaderSubscribe = () => {
  try {
    let appscheck:any = localStorage.getItem('Apps');
    if(appscheck) {
      let json = JSON.parse(appscheck)
      if (json?.POS?.barcodeReader && json?.POS?.isVisible) {
        barcodeReaderID.value = json.POS.barcodeReader
        pb.collection('barcodeReaders').subscribe(barcodeReaderID.value, async (result: {
          action: string,
          record: any
        }) => {
          if (result?.action == 'update' && result?.record?.lastType == "Gift Voucher") {
            giftVoucherId.value = result?.record?.lastRead
            await validateGiftVoucher()
          }
        })
      }
    }
  }catch(e){}
}

const addToDocket = (allProps:any) => {
  docketBookingId.value = allProps.bookingId
  docketTotalDue.value = allProps.totalDue
  isAddToMyTabDialogVisible.value = true
}

const payEventOnDocket = async (params:any,docketId:any) => {
  const { eventId, loggedInUser, activityMap, activityDiscounts } = params;
  const eventData = await pb.collection("events").getOne(eventId, {
    expand: "bookings",
  });

  for (const booking of eventData?.expand?.bookings || []) {
    // Apply discount data (mirrors eventDuePayByPos logic) so lineTotal is computed correctly
    const activityDiscount = activityDiscounts?.[booking?.activity] || {
      docketDiscount: 0,
      discountApplied: "None",
      lineDiscountTotal: 0,
    };
    booking.docketDiscount = activityDiscount.docketDiscount;
    booking.discountApplied = activityDiscount.discountApplied;
    booking.lineDiscountTotal = activityDiscount.lineDiscountTotal;

    // Process the booking and add docketLines under the same docket
    await processBooking(
        booking,
        docketId,
        loggedInUser,
        activityMap,
    );
  }
}

const handleAddToMyTab = async (val:any) => {
  // For events, staying on the same docket still needs the recalculated due synced —
  // so that case must fall through to the event handling below instead of short-circuiting.
  const isSameEventDocket = props?.type === "event" && !!docketId.value && docketId.value === val;

  // If the docket being added to is the same as the current docket
  if(docketId.value && docketId.value == val && !isSameEventDocket){
    emit("closeDialog", false);
    await router.push({
      name: "pos",
      query: {
        did: val,
      },
    });
    return
  }

  // Handle events
  if(props?.type === "event"){
    const details = {
      eventId: props?.eventId,
      loggedInUser: props?.loggedInUserId,
      activityMap: props?.activityMap,
      activityDiscounts: props?.activityDiscounts,
      due: props?.totalDue,
      location: props?.location,
      owner: props?.owner,
    } as any;

    if (isSameEventDocket) {
      await payEventOnDocket(details, val)
    } else if (docketId.value) {
      try {
        await mergeDockets([docketId.value], val)
      } catch (e) {
        console.error("Could not merge old docket", e)
      }
    } else {
      await payEventOnDocket(details, val)
    }

    emit("closeDialog", false);
    await router.push({
      name: "pos",
      query: {
        did: val,
      },
    });
    return
  }

  // If there is already a docket merge it into the new docket
  // else create new docketLines on the new docket
  if (docketId.value) {
    try {
      await mergeDockets([docketId.value], val)
      await pb.collection("bookings").update(docketBookingId.value, { state: "Confirmed" })
    } catch (e) {
      console.error("Could not merge old docket", e)
    }
  } else {
    const productCost = props.products?.reduce((sum: number, product: any) => {
      return sum + (product.qty || 1) * (product.itemPrice ?? product.sellPrice ?? 0);
    }, 0) || 0;
    const bookingCost = props?.totalDue - productCost;
    const discountRate = props?.discountBookings || 0; // percentage discount
    const discountProducts = props?.discMemProducts || 0

    // Base amount before tod and discount, used to derive sessionPrice and qty
    const todMultiplier = 1 + (props.todAdjustment || 0) / 100;
    const preTodPreDiscountAmount = (bookingCost + Number(props.lineDiscountTotal || 0)) / todMultiplier;

    let sessionPrice = preTodPreDiscountAmount; // fallback: base price without tod or discount
    try {
      let durationCheck = await pb.collection("bookings").getOne(docketBookingId.value, {expand:'activity'})
      if(durationCheck && durationCheck?.expand?.activity?.sessionFullPrice){
        sessionPrice = Math.floor(durationCheck.expand.activity.sessionFullPrice)
      }
    } catch(e){}

    let quantity = Math.floor(preTodPreDiscountAmount / sessionPrice)
    if(quantity < 1){
      quantity = 1
    }

    const posSetup = await posSetupStore.load();

    const data = {
      docket: val,
      transactionType: "Booking Payment",
      qty: quantity,
      itemPrice: sessionPrice,          // Booking Price to Go Here
      docketDiscount: discountRate,           // Booking Discount to Go Here
      todAdjustment: props.todAdjustment,
      todSource: props.todSource,
      discountApplied: props.discountApplied || "None",
      lineDiscountTotal: props.lineDiscountTotal.toFixed(2),
      lineTotal: bookingCost,
      createdBy: userId.value,
      booking: docketBookingId.value,   // Booking ID to Go Here
      posDevice: posSetup?.id || "" // POS DEVICE to Go Here
    };

    try {
      let DocketLine = await pb.collection("docketLines").create(data); // Creating New Line
      // FEEDBACK - Add Docket Line To Booking
      let BookingDetails = await pb.collection("bookings").update(docketBookingId.value, {docketLine: DocketLine.id, state:"Confirmed"});

      // Add products
      if (props.products && props.products.length > 0) {
        for (const product of props.products) {

          if (product) {
            const productDocketLineData = {
              docket: val,
              transactionType: "Product",
              product: product.id,
              qty: product.qty || 1,
              itemPrice: product.sellPrice,
              docketDiscount: discountProducts,
              discountApplied: product.discountApplied,
              todAdjustment: product.todAdjustment,
              todSource: product.todSource,
              lineDiscountTotal: product.lineDiscountTotal.toFixed(2),
              lineTotal: (product.qty || 1) * product.itemPrice,
              status: "Available",
              createdBy: userId.value,
              booking: docketBookingId.value,
            };

            // Create a new docket line for each product
            const productDocketLine = await pb.collection("docketLines").create(productDocketLineData);
          }
        }
      }

      // Update Main Docket
      await pb.collection("dockets").update(val, {
        discountSources: props?.discountSources,
        discountEvents: props?.discountEvents,
        discountProducts: props.discountProduct,
        user: BookingDetails.users[0],
      });
    } catch (e) {
      console.error("Cannot Create Docket", e)
    }
  }

  // Close the Dialog and go to pos
  emit("closeDialog", false);
  router.push({
    name: "pos",
    query: {
      did: val,
    },
  });
}

// computed
const ownerUser = computed(() => usersList.value?.find((user) => user.id === props?.owner));

const canGoNegative = computed(() => {
  return (
    (!!currentLocationDetails.value?.allowUserNegAccountBalance && !!props?.userCreditCards?.length) ||
    !!ownerUser.value?.dontDailySettleAccount ||
    globalSetup.value.dontDailySettleAccounts
  );
});

const getuserCreditAmt = computed(() => {
  const userFound = ownerUser.value;
  if (!userFound) {
    return {
      paying: 0,
      originalAmt: 0,
    };
  }

  const balance = accountType.value === 'account' ? userFound.tempBalance || 0 : userFound.loyaltyBalance || 0;

  // Loyalty: never go negative, clamp to [0, totalDue]
  // Account + canGoNegative: default paying to totalDue (balance may be negative)
  // Account + no negative: clamp to [0, totalDue]
  let paying: number;
  if (accountType.value !== 'account') {
    paying = Math.min(Math.max(balance, 0), props.totalDue);
  } else if (canGoNegative.value) {
    paying = props.totalDue;
  } else {
    paying = Math.min(Math.max(balance, 0), props.totalDue);
  }

  return {
    paying,
    originalAmt: balance,
  };
});

const creditAmtTopay = ref<number>(getuserCreditAmt.value?.paying ?? 0);

const canPayWithAccount = computed(() => {
  const userFound = ownerUser.value;
  if (!userFound) {
    return false
  }

  const tempBalance = userFound.tempBalance || 0;
  const loyaltyBalance = userFound.loyaltyBalance || 0;

  if (loyaltyBalance > 0) {
    return true;
  }

  if (canGoNegative.value || tempBalance > 0) {
    return true;
  }
  return false;
})

const creditLimitMax = computed(() => {
  if (accountType.value !== 'account') {
    // Loyalty balance — never allow going below zero
    return getuserCreditAmt.value.originalAmt;
  }
  // Account/tempBalance — allow totalDue only if the account can go negative
  return canGoNegative.value ? props.totalDue : getuserCreditAmt.value.originalAmt;
})

const isMembershipCouponBlockedForOwner = computed(() => {
  return isMembershipCouponBlockedForUser(bookingOwnerUser.value);
});

const refreshGiftVoucherAvailability = async () => {
  try {
    const response = await pb.collection("giftVouchers").getList(1, 1, {
      fields: "id",
      skipTotal: true,
      requestKey: null,
    });
    hasGiftVoucherRecords.value = response.items.length > 0;
  } catch (error) {
    console.error("Error checking gift voucher availability:", error);
  }
};

const getCoupons = computed(() => {
  let bookingDate = moment(props?.bookingProp?.startTime);

  return (
    couponList.value?.filter((coupon: any) => {
      let dc = moment(coupon?.validFrom)
      let dcEnd = moment(coupon?.expiryTime)
      // Filter out after hours coupons if not after hours
      if(coupon?.afterHoursOnly === true && !props.afterHours) return false
      // Filter out coupons restricted to specific activities that don't include this booking's activity
      if (coupon?.activity?.length && props?.bookingProp?.activity && !coupon.activity.includes(props.bookingProp.activity)) return false
      // Filter out membership coupons if no membership or membership benefits are paused or their membership doesnt use membership coupons
      if (isMembershipCouponBlockedForOwner.value && isMembershipCoupon(coupon)) return false;
      return coupon.user === couponUser.value && (props?.loggedInUserId || userId?.value)
        && (!coupon?.validFrom || coupon?.validFrom == "" || bookingDate.isSameOrAfter(dc))
       && bookingDate.isSameOrBefore(dcEnd)
    }) || []);
});

const usedCoupons:Ref<any[]> = ref([])
const getListCoupons = computed(() => {
  let couponsClean:any = [];
  getCoupons.value.forEach(c => {
    let cCopy = JSON.parse(JSON.stringify(c))
    usedCoupons.value.forEach(coup => {
      if (coup.coupon == cCopy.id) {
        cCopy.qtyRemaining = Math.floor(cCopy.qtyRemaining)
      }
    })
    couponsClean.push(cCopy)
  })
  return couponsClean.filter((c:any) => c.qtyRemaining > 0)
})

const tabs = computed<Tab[]>(() => {
  const hasExistingDocket = props?.bookingProp?.docketId || props?.bookingProp?.expand?.docketLine?.docket;
  const hidePaymentTabs = props.haveCreatePermission && hasExistingDocket;
  const isStaff = props.haveCreatePermission;

  return [
    {
      value: "pos",
      label: "Pos",
      icon: "mdi-network-pos",
      visible: isStaff && canPayAtPos.value,
    },
    {
      value: "coupon",
      label: "Coupon",
      icon: "mdi-ticket-percent-outline",
      visible: !hidePaymentTabs && props?.type !== "event" && props.owner && getListCoupons.value.length > 0,
    },
    {
      value: "giftVoucher",
      label: "Gift Voucher",
      icon: "mdi-wallet-giftcard",
      visible: !hidePaymentTabs && props?.type !== "event" && hasGiftVoucherRecords.value,
    },
    {
      value: "creditCard",
      label: "Credit Card",
      icon: "mdi-credit-card-outline",
      visible:
        !hidePaymentTabs &&
        props?.type !== "event" &&
        currentLocationDetails.value?.onlineCreditCardsEnabled
    },
    {
      value: "waiveFee",
      label: "Waive Fee",
      icon: "mdi-gift-open-outline",
      visible: isStaff && !hidePaymentTabs && ["event", "Booking"].includes(props?.type),
    },
    {
      value: "payInStore",
      label: "Pay In Store",
      icon: "mdi-store-marker",
      visible: !!props.canPayInStore,
    },
    {
      value: "userCredit",
      label: "User Account",
      icon: "mdi-account-credit-card-outline",
      visible:
        !hidePaymentTabs &&
        props?.type !== "event" &&
        canPayWithAccount.value &&
        (isStaff ? props?.owner != pb?.authStore?.model?.id : props?.owner === pb?.authStore?.model?.id),
    },
  ].filter((tab) => tab.visible);
});

const currentTab = ref<string>(tabs.value[0]?.value || "");

watch(tabs, (availableTabs) => {
  if (!availableTabs.some(tab => tab.value === currentTab.value)) {
    currentTab.value = availableTabs[0]?.value || "";
  }
});

watch(currentTab, (value) => {
  emit("paymentTabChange", value);
}, { immediate: true });

// Computed
/*
  This computed property calculates the surcharge amount based on the selected location's card surcharge fixed fee and percentage fee.
*/
const getSurchargeAmount = computed(() => {
  const selectedLocation = locationList.value?.find(
    (l: {
      id: string;
      cardSurchargeFixedFee?: number;
      cardSurchargePercentFee?: number;
    }) => l.id === currentLocation.value,
  );

  // Destructure with default values to ensure they're numbers
  const { cardSurchargeFixedFee = 0, cardSurchargePercentFee = 0 } =
    selectedLocation || {};

  let amount = props?.bookingAmount - parseFloat(props?.totalPaid || 0)
  // Calculate surcharge fee (fixed fee + percentage of the checkout total)
  const surchargeFee =
    cardSurchargeFixedFee +
    amount * (cardSurchargePercentFee / 100);

  // Return the final result formatted to two decimal places
  return surchargeFee.toFixed(2);
});

// method
const updateErrorMessage = (
  isVisible: boolean,
  message: string,
  color: any = null,
  icon: any = null,
) => {
  emit("updateMessage", {
    isVisible,
    message,
    color: color || "error",
    icon: icon || "tabler-xbox-x",
  });
};

/**
 * Validates the gift voucher entered by the user.
 *
 * @returns {Promise<void>}
 */
const validateGiftVoucher = async () => {
  try {
    // If no gift voucher ID is provided, return early
    if (!giftVoucherId.value) return;

    // Fetch the gift voucher details from the database
    const response = await pb.collection("giftVouchers").getOne(giftVoucherId.value);

    if (response) {
      // Store the gift voucher details
      appliedGiftVoucherDetails.value = response;

      // Check if gift voucher is past expire date
      const startOfToday = moment().startOf("day");
      if (moment(response?.expiry).isBefore(startOfToday)) {
        isInvalidCoupon.value = true;
        updateErrorMessage(true, "Gift Voucher has expired");
      } else if (response?.balanceRemaining <= 0) {
      // If the gift voucher balance is 0, display an error message
        isInvalidCoupon.value = true;
        updateErrorMessage(true, "Gift Voucher has 0 Balance");
      } else {
        // If the gift voucher balance is valid, update the amount to redeem
        isInvalidCoupon.value = false;

        // Calculate the minimum value between balanceRemaining and booking due amount
        amountToRedeem.value = Math.min(
          response.balanceRemaining,
          props?.totalDue || 0,
        );
        updateErrorMessage(false, "");
      }
    }
  } catch (e) {
    // If the gift voucher is not found, display an error message
    isInvalidCoupon.value = true;
    updateErrorMessage(true, "Gift Voucher Not Found");
  }
};

const createPendingPaymentRecord = async () => {
  const { valid } = (await refVFormCreditCard.value?.validate?.()) || {
    valid: true,
  };

  if (props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)) {
    if (!valid) return;
  } else {
    selectedCreditCard.value = "newCard";
  }

  isProcessingCardDialogVisible.value = true;

  try {
    addCardDialogMessage.value = {
      isVisible: true,
      spinner: true,
      message: "",
      color: "",
    };

    const browserInfo = await getBrowserInfo();

    const paymentData = {
      docket: docketId.value,
      amount: Round2String(props?.totalDue, 2),
      paymentType: "Credit Card",
      addCreditCard:
        saveNewCard.value && selectedCreditCard?.value === "newCard",
      billingEmail: pb?.authStore?.model?.email || "",
      billingPhone: pb?.authStore?.model?.mobileNumber || "",
      cardToken:
        selectedCreditCard?.value !== "newCard"
          ? selectedCreditCard?.value
          : "",
      user: props?.owner || "",
      status: "Pending",
      createdBy: userId.value,
      surchargeAmount: parseFloat(Number(getSurchargeAmount.value).toFixed(2)),
      source: props?.source,
      webResponseLoc: window.location.origin,
      browser: browserInfo.browser,
      OS: browserInfo.os,
      ipAddress: browserInfo.ip,
    };

    // Create the payment record in the database
    const paymentRecord = await pb.collection("docketPayments").create(paymentData);
    payingDocket.value = paymentRecord?.id;

    // Subscribe to the created payment record
    await subscribeBookingPayment(paymentRecord?.id);
  } catch (error) {
    console.error("Error creating pending payment record", error);
  }
};

const goToPos = async (docketId:any) => {
  let docketCheckID = docketId
  try{
    let docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + props?.bookingId + '"', sort:"created", expand:"docket"})
    if(docketLines && docketLines.length > 0) {
      docketCheckID = docketLines[docketLines.length - 1]?.docket || docketCheckID
    }
  }catch(e){
    console.error(e)
  }

  emit("goToPosPayment", docketCheckID);
}

const checkBookingBalance = async (type: string) => {
  // Guard against double-click/double-tap firing this before processingPayment
  // is set further down the chain (handleApplyProcess) - without this, two
  // rapid clicks can both pass the "already paid" guard in handleInitiateFlow
  // and each independently deduct the coupon/gift voucher balance.
  if (processingPayment.value) return;
  processingPayment.value = true;
  try {
    await handleInitiateFlow(type)
    emit('isConfirmed', true)
  } catch (error: any) {
    console.error('Error in checkBookingBalance:', error)
    updateErrorMessage(
      true,
      error?.message || "Failed to process payment. Please try again.",
      "error",
      "tabler-xbox-x"
    );
    emit('isConfirmed', true)
  } finally {
    processingPayment.value = false;
  }
}

const handleInitiateFlow = async (type: string) => {
  try {
    updateErrorMessage(
      true,
      "Payment Inprogress...",
      "warning",
      "mdi-account-credit-card",
    );

    // Validate form based on type
    if (["Coupon", "GiftVoucher", "WaiveFee", "UserCredit"].includes(type)) {
      const form = {
        Coupon: refVFormCoupon,
        GiftVoucher: refVFormGiftVoucher,
        WaiveFee: refVFormWaive,
        UserCredit: refCreditAmtForm,
      }[type]?.value;

      const { valid } = (await form?.validate?.()) || { valid: true };

      if (!valid) return;
    }

    if(props?.bookingId){
      try{
        // check if Booking is Confirmed
        let getBookingDetails = await pb.collection("bookings").getOne(props?.bookingId)

        // Guard: prevent duplicate payment if booking is already fully paid in the DB
        if (getBookingDetails.due <= 0 && getBookingDetails.paid > 0) {
          emit("bookingChange", props?.bookingId);
          toast.error("Booking already paid for", {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          return;
        }

        if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
          if(type == "CreditCard" || type == "GiftVoucher" || type == "Coupon" || type == "WaiveFee" || type == "UserCredit"){
            await pb.collection("bookings").update(props?.bookingId, {state: 'Processing'});
          } else {
            await pb.collection("bookings").update(props?.bookingId, {state: 'Confirmed'});
          }
        }
      } catch(e){
        alert("Error With Booking - Please Start the Booking for a Bay Again")
        return
      }
    }

    if (props?.type === "Booking") {
      let amount = props?.bookingAmount || 0
      let qty = Math.floor(props?.bookingSlot?.slotQty) || 1
      let sessionPrice = props?.bookingSlot?.sessionPrice || 0;
      if(props.bookingAmount && props?.bookingSlot?.sessionPrice){
        qty = Math.floor(props.bookingAmount / props?.bookingSlot?.sessionPrice)
      }

      if(props?.bookingId && props?.bookingAmount){
        try{
          let docketLineCheck = await pb.collection("bookings").getOne(props?.bookingId, {expand:'activity'})
          const activity = docketLineCheck?.expand?.activity
          if(docketLineCheck && activity?.sessionFullPrice && activity?.sessionDuration){
            qty = Math.ceil(docketLineCheck.duration / activity.sessionDuration)
            sessionPrice = activity.sessionFullPrice
          }
        }catch(e){}

        // Has there been a past booking
        // Is this a booking for minutes
        let doingMinuteSales = false
        try{
          let docketLines = await pb.collection("docketLines").getFullList({filter: 'transactionType="Booking Payment" && booking="' + props?.bookingId + '"', sort:"created"})
          if(docketLines && docketLines.length > 0) {
            docketLines.forEach((dl: any, dli: number) => {
              if (dli < docketLines.length - 1 && dl.itemPrice > 0 && dl.itemPrice >= sessionPrice) {
                qty = qty - dl.qty
                amount = sessionPrice * qty
              } else if (dl.itemPrice > 0 && dl.itemPrice < sessionPrice){ // Actual Minutes Being Sold
                doingMinuteSales = true
                amount += dl.itemPrice * dl.qty
                qty += dl.qty
              }
            })
            docketLineId.value = docketLines[docketLines.length - 1].id
            docketId.value = docketLines[docketLines.length - 1].docket // UPDATE DOCKET
            if(doingMinuteSales){
              sessionPrice = amount;
            }
          }
        }catch(e){console.error(e)}
      }

      if(qty < 1){
        qty = 1
      }

      const bookingDetails = {
        location: props?.location,
        discountProduct: props?.discountProduct,
        discountBookings: props?.discountBookings,
        discountEvents: props?.discountEvents,
        discountSources: props?.discountSources,
        discCodeBookings: props?.discCodeBookings,
        discCodeProduct: props?.discCodeProduct,
        discMemBookings: props?.discMemBookings,
        discMemProducts: props?.discMemProducts,
        todAdjustment: props?.todAdjustment,
        todSource: props?.todSource,
        discountApplied: props?.discountApplied,
        lineDiscountTotal: props?.lineDiscountTotal,
        due: props?.totalDue,
        loginUser: props?.loggedInUserId || props?.owner,
        amount: amount,
        owner: props?.owner,
        paid: props?.totalPaid,
        qty: qty,
        itemPrice: sessionPrice,
        payMethods: props?.payMethods,
        startTime: props?.bookingProp?.startTime
      };

      try {
        await initiateDocketForBooking(
          bookingDetails,
          props?.bookingId,
          props?.source,
          props?.products,
        );
      } catch (err: any) {
        // Don't proceed to charge a coupon/gift voucher/card against a docket
        // that failed to be created or updated
        updateErrorMessage(
          true,
          err?.message || "Failed to set up the docket for this booking. Please try again.",
          "error",
          "tabler-xbox-x",
        );
        return;
      }

      try {
        // Handle different payment types
        switch (type) {
          case "Coupon":
          case "GiftVoucher":
            await handleApplyProcess(type);
            emit("bookingChange", props?.bookingId);
            emit("paymentSuccess", true);
            break;
          case "pos":
            emit("closeDialog", false);
            emit("goToPosPayment", docketId.value);
            break;
          case "CreditCard":
            await createPendingPaymentRecord();
            break;
          case "UserCredit":
          case "WaiveFee":
            const paymentData = {
              ...bookingDetails,
              creditAmt: creditAmtTopay.value,
            };
            const paymentType = type === "WaiveFee" ? "Fee Waived" : accountType.value === 'account' ? "User Account" : "User Loyalty";

            await handleUserCreditAndWaiveFeePayment(
              paymentData,
              props?.bookingId,
              paymentType,
              feeWaiveReason.value,
              props?.source,
            );

            emit("bookingChange", props?.bookingId);
            emit("paymentSuccess", true);
            await getUserList(true);
            break;
        }
      } catch (err: any) {
        if (err?.message)
          updateErrorMessage(true, err?.message, "error", "tabler-xbox-x");
      }

      return;
    } else if (props?.type === "event") {
      const details = {
        eventId: props?.eventId,
        loggedInUser: props?.loggedInUserId,
        activityMap: props?.activityMap,
        due: props?.totalDue,
        total: props?.total,
        location: props?.location,
        owner: props?.owner,
        activityDiscounts: props?.activityDiscounts,
        notes: feeWaiveReason.value,
      } as any;

      switch (type) {
        case "pos":
          const eventDocketId = await eventDuePayByPos(details);
          emit("goToPosPayment", eventDocketId);
          return;
        case "WaiveFee":
          await eventDuePayByWaiveFee(details);
          emit("paymentSuccess", true);
          break;
      }
    } else {
      // coupon/giftVoucher/userCredit
      const paymentDetails = {
        ...props?.requestedPaymentDetails,
        owner: props?.owner,
        loggedInUserId: props?.loggedInUserId,
        location: props?.location,
        identifier: giftVoucherId.value,
        emailAddress: props?.emailAddress,
      };

      await createEditDocketForCustomizePurchase(paymentDetails, props?.type);

      // Handle different payment types
      try {
        switch (type) {
          case "GiftVoucher":
            paymentDetails.amount = amountToRedeem.value;
            await payByGiftVoucher(paymentDetails, props?.type, props?.source);
            break;
          case "UserCredit":
            await payByUserCredit(paymentDetails, props?.type, props?.source, {}, accountType.value);
            await getUserList(true);
            break;
          case "pos":
            await payByPos(paymentDetails, props?.type);
            emit("goToPosPayment", docketId.value);
            return;
        }
        toast.success("Payment successful", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        docketId.value = "";
        docketLineId.value = "";
        emit("paymentApproved", false);
        emit("closeDialog", false);
      } catch (e) {
        if (!lowBalanceError?.value) {
          toast.error("Something went wrong.", {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          docketId.value = "";
          docketLineId.value = "";
          emit("closeDialog", false);
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const getDocketsInUse = async () => {
  usedCoupons.value = [];
  try {
    if (props.bookingId) {
      // Get DocketLine With That Booking ID
      let getDocketLine = await pb.collection("docketLines").getFirstListItem('booking="' + props.bookingId + '"')
      if (getDocketLine && getDocketLine.docket) {
        let docketList = await pb.collection("docketPayments").getFullList({filter: 'paymentType ="Coupon" && docket="' + getDocketLine.docket + '"'});
        docketList.forEach(coup => {
          usedCoupons.value.push({id: coup.docket, coupon: coup.coupon})
        })
      }
    }
  } catch (e) {
  }
}

const MaxCouponsAllowed = ref(1)
const MaxCouponsToUse = async () => {
  let maxByActivity = 1;
  let couponValue = props?.couponValue ?? 1;

  if (props?.bookingProp?.activity) {
    try {
      const activity = await pb.collection('activities').getOne(props.bookingProp.activity);
      if (activity?.couponCost && activity.couponCost > 0) {
        maxByActivity = Math.floor(activity.couponCost);
        if (props?.couponCost) {
          maxByActivity = props.couponCost;
        }
      }

      if (props?.bookingSlot?.slotQty) {
        maxByActivity = maxByActivity * props.bookingSlot.slotQty;
      } else if (props?.bookingProp?.duration && activity?.sessionDuration) {
        maxByActivity = maxByActivity * Math.floor(props.bookingProp.duration / activity.sessionDuration);
      }

      const couponCost = props?.couponCost || activity?.couponCost || props?.couponValue || 1;
      couponValue = activity?.sessionFullPrice ? activity.sessionFullPrice / couponCost : couponCost;
    } catch(e){}
  }

  const todDiscountVal = props.todAdjustment < 0 ? -props.todAdjustment : 0;
  const docketDiscount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;
  const discount = todDiscountVal > docketDiscount ? todDiscountVal : docketDiscount || 0;
  const couponValueAfterDiscount = Number(((couponValue * (100 - discount)) / 100).toFixed(2));
  const bookingDue = Number(props?.bookingProp?.due ?? 0);
  const totalDue = Number(props?.totalDue ?? 0);
  const remainingDueCandidates = [bookingDue, totalDue].filter((value) => value > 0);
  const remainingDue = remainingDueCandidates.length ? Math.min(...remainingDueCandidates) : 0;

  if (remainingDue <= 0 || couponValueAfterDiscount <= 0) {
    MaxCouponsAllowed.value = 0;
    return;
  }

  const neededCoupons = Math.ceil(remainingDue / couponValueAfterDiscount);
  MaxCouponsAllowed.value = Math.min(maxByActivity, neededCoupons);
}

const getQuantityList = computed(() => {
  const coupon = getListCoupons.value.find((c: any) => c.id === selectedCoupon.value);
  const max = Math.min(coupon?.qtyRemaining || 0, MaxCouponsAllowed.value);
  return Array.from({ length: max }, (_, i) => i + 1);
});

watch(selectedCoupon, async () => {
  await MaxCouponsToUse();
  const availableQuantities = getQuantityList.value;
  quantity.value = availableQuantities.length
    ? availableQuantities[availableQuantities.length - 1]
    : null;
});

watch(getQuantityList, (availableQuantities) => {
  if (!availableQuantities?.some((el: any) => el === quantity.value)) {
    quantity.value = availableQuantities.length
      ? availableQuantities[availableQuantities.length - 1]
      : null;
  }
});

/**
 * Handles the payment process for either a coupon or a gift voucher.
 *
 * @param {string} type - The type of payment to be processed. It can be either 'Coupon' or 'Gift Voucher'.
 *
 * @returns {Promise<void>} - A promise that resolves when the payment process is completed successfully.
 *
 * @throws {Error} - Throws an error if there is an issue with the payment process.
 */
const handleApplyProcess = async (type: string) => {
  processingPayment.value = true;
  try {
    let amount: number;
    let paymentType: "Gift Voucher" | "Coupon";
    let identifier;

    if (type === "Coupon") {
      const selectedCouponRecord = getListCoupons.value.find(
        (coupon: any) => coupon.id === selectedCoupon.value,
      );

      if (!selectedCouponRecord) {
        isInvalidCoupon.value = true;
        updateErrorMessage(true, "Coupon not found or unavailable");
        return;
      }

      if (isMembershipCouponBlockedForOwner.value && isMembershipCoupon(selectedCouponRecord)) {
        isInvalidCoupon.value = true;
        updateErrorMessage(true, "Membership coupons cannot be used for this user");
        return;
      }

      // COUPON CAN ONLY PAY FOR BOOKING - NOT PRODUCTS (EXTRAS)
      // If Not props.couponValue = work it out again
      let sessionPrice = 0
      if(props?.bookingProp?.activity){
        try {
          let bookingValueCheck = await pb.collection("activities").getOne(props?.bookingProp?.activity)
          if(bookingValueCheck && bookingValueCheck.sessionFullPrice){
            sessionPrice = bookingValueCheck.sessionFullPrice / bookingValueCheck.couponCost
            if(props?.couponCost){
              sessionPrice = bookingValueCheck.sessionFullPrice / props.couponCost
            }
          }
        } catch(e){}
      }

      const couponValue = sessionPrice ? sessionPrice : props?.couponValue ?? 1;
      const todDiscountVal = props.todAdjustment < 0 ? -props.todAdjustment : 0;
      const docketDiscount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;
      const discount = todDiscountVal > docketDiscount ? todDiscountVal : docketDiscount || 0;
      // Calculate the raw amount
      let rawAmount = (couponValue * quantity.value * (100 - discount)) / 100;
      
      // Round normally, but cap at totalDue to prevent overpayment
      amount = Math.min(
        parseFloat(rawAmount.toFixed(2)),
        props?.totalDue ?? rawAmount
      );

      paymentType = "Coupon";
      identifier = selectedCoupon.value;
    } else {
      amount = parseFloat(Number(amountToRedeem.value).toFixed(2));
      paymentType = "Gift Voucher";
      identifier = giftVoucherId.value;
    }

    const params = {
      status: "Approved",
      paymentType,
      amount,
      identifier,
      qty: quantity.value,
      toPay: props?.totalDue,
      type: props?.type,
      bookingId: props?.bookingId,
      totalPaid: props?.totalPaid,
      userId: props?.loggedInUserId || props?.owner,
      payMethods: props?.payMethods,
      source: props?.source,
      giftValue: appliedGiftVoucherDetails.value,
    };

    try {

      const response = await payWithGiftVoucherOrCoupon(params);

      if (response) {
        clearForm();
        await getCouponList();
        await getDocketsInUse()
      }
    } catch (e) {}
  } catch (e) {
    console.error(e);
  } finally {
    processingPayment.value = false;
  }
};

const handleDiscountCode = async (code: string) => {
  try {
    updateErrorMessage(true, "Processing your request...", "warning", "");

    // Determine booking or event type details and prepare data accordingly
    const isBooking = props?.type === "Booking";

    // Check if docket already exists
    if (!docketId.value) {
      const details = isBooking
        ? {
            location: props?.location,
            discountProduct: props?.discountProduct,
            discountBookings: props?.discountBookings,
            discountEvents: props?.discountEvents,
            discCodeBookings: props?.discCodeBookings,
            discCodeProduct: props?.discCodeProduct,
            discMemBookings: props?.discMemBookings,
            discMemProducts: props?.discMemProducts,
            due: props?.totalDue,
            loginUser: props?.loggedInUserId,
            amount: props?.bookingAmount,
            owner: props?.owner,
            paid: props?.totalPaid,
            qty: props?.bookingSlot?.slotQty,
            itemPrice: props?.bookingSlot?.sessionPrice,
            payMethods: props?.payMethods,
            todAdjustment: props?.todAdjustment,
            discountApplied: props?.discountApplied,
            lineDiscountTotal: props?.lineDiscountTotal,
          }
        : {
          eventId: props?.eventId,
          discountProduct: props?.discountProduct,
          discountBookings: props?.discountBookings,
          discountEvents: props?.discountEvents,
          discCodeBookings: props?.discCodeBookings,
          discCodeProduct: props?.discCodeProduct,
          discMemBookings: props?.discMemBookings,
          discMemProducts: props?.discMemProducts,
          loggedInUser: props?.loggedInUserId,
          activityMap: props?.activityMap,
          due: props?.totalDue,
          location: props?.location,
          owner: props?.owner,
          startTime: props?.bookingProp?.startTime,
        };

      // Initiate docket if not present
      if (isBooking) {
        await initiateDocketForBooking(
          details,
          props?.bookingId,
          props?.source,
          props?.products,
        );
      } else {
        await initiateDocketForEvent({
          ...details,
          activityDiscounts: props?.activityDiscounts
        });
      }
    }

    // Check if the discount Code is Valid for the booking Date
    let canApply = await validateBookingDateAppliedDiscountCode(props?.bookingProp);

    if(canApply) {
      // Apply discount code after ensuring docket exists
      await handleDiscountCodeApply(
        docketId.value,
        props?.bookingProp?.startDate,
      );

      // Update booking's due amount from the docket
      if (props?.bookingId && docketId.value) {
        const docket = await pb.collection("dockets").getOne(docketId.value);
        if (docket) {
          await pb.collection("bookings").update(props.bookingId, { due: docket.due });

          // Only confirm the booking once the discount has actually cleared the balance owing
          if (docket.due <= 0) {
            await confirmBooking();
          }
        }
      }
    }

    emit("refetch", true);
  } catch (e) {
    console.error("Error applying discount code:", e);
  }
};

/**
 * Handles the error scenario when the user's credit balance is insufficient.
 * It checks if the user has any credit cards and displays appropriate error messages.
 * If no cards exist, it prompts the user to add a new card.
 *
 * @returns {Promise<void>} - A promise that resolves when the error handling process is completed.
 *
 * @throws {Error} - Throws an error if there is an issue with checking the user's cards.
 */
const handleCreditError = async () => {
  try {
    // Check if any card exists
    const resultList = await pb.collection("bankCards").getList(1, 50, {
      filter: `user = "${props?.owner}"`,
    });

    const cards = resultList?.items || [];
    const primaryCard = cards.find((card: any) => card?.isPrimary);

    if (primaryCard) {
      // Extract the last 4 digits of the card number
      const lastFourDigits = primaryCard.cardNum.slice(-4);

      updateErrorMessage(
        true,
        `Insufficient balance. The remaining amount will be charged to your card ending in ${lastFourDigits}.`,
        "error",
        null,
      );
    } else if (cards.length) {
      // If there are cards but no primary card, notify the user
      updateErrorMessage(
        true,
        "Insufficient balance. No primary card found. Please add a primary card.",
        "error",
        null,
      );
    } else {
      // If no cards exist, prompt the user to add a new card
      updateErrorMessage(
        true,
        "Insufficient balance and no credit card on file. Please add a new card into your account.",
        "error",
        null,
      );
      setTimeout(() => {
        emit("addNewCardDialog", true);
      }, 2000);
    }
  } catch (e) {
    updateErrorMessage(
      true,
      "An error occurred while checking your cards. Please try again later.",
      "error",
      null,
    );
    if (props?.loggedInUserId) {
      emit("closeDialog", false);
    }
  } finally {
    setTimeout(() => {
      clearForm();
      if (props?.loggedInUserId) {
        emit("closeDialog", false);
      }
    }, 3000);
  }
};

// This function is used to close the processing card dialog.
const closeProcessingCardDialog = async () => {
  try {
    // Fetch the current record for the docket payment
    const currRecord = await pb.collection("docketPayments").getOne(`${payingDocket.value}`);

    if (currRecord?.status === "Pending") {
      // Check if the payment is in "Pending" status
      const updatedRecord = await pb.collection("docketPayments").update(`${payingDocket.value}`, {
          status: "Canceled",
        });

      // Update dialog message if the record was successfully updated
      if (updatedRecord) {
        addCardDialogMessage.value = {
          isVisible: true,
          color: "error",
          message: `Payment ${updatedRecord.status}.`,
          spinner: false,
        };
        isProcessingCardDialogVisible.value = false;
        return;
      }
    }
  } catch (error) {
    console.error("Error cancelling docket payment:", error);
  } finally {
    // Ensure dialog visibility and message are reset in all cases
    isProcessingCardDialogVisible.value = false;
    addCardDialogMessage.value = {
      isVisible: false,
      color: "",
      message: "",
      spinner: false,
    };
  }
};

const clearForm = () => {
  selectedCoupon.value = null;
  quantity.value = null;
  giftVoucherId.value = "";
  appliedGiftVoucherDetails.value = "";
  amountToRedeem.value = undefined;
  isInvalidCoupon.value = false;
  lowBalanceError.value = false;
};

// watcher
watch(accountType, () => {
  creditAmtTopay.value = getuserCreditAmt.value.paying;
});

watch(
  () => [props?.owner, lowBalanceError?.value],
  async ([newOwner, newLowBalanceError]) => {
    // Handle owner changes
    await getCouponList();

    creditAmtTopay.value = getuserCreditAmt.value.paying;
    // Handle lowBalanceError changes
    if (newLowBalanceError) await handleCreditError();
  },
  { immediate: true },
);

watch(() => [
  props.totalDue,
  props.totalPaid,
  props?.bookingProp?.due,
  props?.bookingProp?.value,
  props?.bookingDiscount,
  props?.discountBookings,
  props?.todAdjustment,
  props?.couponCost,
  props?.bookingProp?.duration,
  props?.bookingSlot?.slotQty,
], async () => {
  await totalDueIsNotProducts()
  await MaxCouponsToUse()
});

/**
 * Check Product Costs - Remove these From Coupons
 */
const totalDueNotProducts = ref(true)
const totalDueIsNotProducts = async() => {
  totalDueNotProducts.value = true
  let sessionPrice = 0

  if(props?.bookingProp?.activity){
    try {
      let bookingValueCheck = await pb.collection("activities").getOne(props?.bookingProp?.activity)
      if(bookingValueCheck && bookingValueCheck.sessionFullPrice){
        sessionPrice = bookingValueCheck.sessionFullPrice / (bookingValueCheck.couponCost || 1)
        if(props?.couponCost){
          sessionPrice = bookingValueCheck.sessionFullPrice / props.couponCost
        }
      }
    } catch(e){}
  }
  const couponValue = sessionPrice ? sessionPrice : props?.couponValue ?? 30

  const todDiscountVal = props.todAdjustment < 0 ? -props.todAdjustment : 0;
  const docketDiscount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;
  const discount = todDiscountVal > docketDiscount ? todDiscountVal : docketDiscount || 0;

  let couponValueAfterDiscount = parseFloat((parseFloat(couponValue) * 1 * (100 - discount)) / 100);

  if(props?.bookingProp?.value) {
    let newAmount = parseFloat(props.bookingProp.value) * (100 - discount) / 100 // Applies to Booking Only
    if (props?.totalPaid) {
      newAmount = newAmount - parseFloat(props.totalPaid)
    } else if(props?.bookingProp?.paid){
      newAmount = newAmount - parseFloat(props.bookingProp.paid)
    }

    if(props?.totalDue == couponValueAfterDiscount || (props?.totalDue * 2 == couponValueAfterDiscount)){
      newAmount = couponValueAfterDiscount
    }
    if (newAmount > 0 && couponValueAfterDiscount > 0) {
      let valueCheck = Math.round(newAmount / couponValueAfterDiscount)
      if (valueCheck < 1) {
        totalDueNotProducts.value = false
      }
    } else {
      totalDueNotProducts.value = false
    }
  } else {
    totalDueNotProducts.value = true
  }
}

const onUpdateMenu = (open:boolean) =>{
  if(open){
    setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
  }
}

const confirmBooking = async () => {
  try {
    let getBookingDetails = await pb.collection("bookings").getOne(props?.bookingId)
    if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
      await pb.collection("bookings").update(props?.bookingId, {state: 'Confirmed'});
    }
    emit('isConfirmed', true)
  } catch(e){}
}

const goToProfile = () => {
  showPreventBenefits.value = false
  emit('closeDialog', false);
  router.push('/user/' + props?.owner)
}

const eventDocketID = ref('')
const getEventDocket = async () => {
  eventDocketID.value = ''
  if(props?.type == "event" && props?.eventId){
    // Get All Bookings
    try {
      let allBookings = await pb.collection("events").getOne(props?.eventId, {fields:'bookings'})
      // Get All DocketLines for Bookings
      let allBookingsQuery = allBookings?.bookings?.map((booking:string) => 'booking="' + booking + '"').join('||')

      let allBookingsCheck = await pb.collection("docketLines").getFullList(
        {
          filter: 'transactionType="Event" && (' + allBookingsQuery + ')',
          expand: 'docket',
          fields: 'docket,expand.docket.status',
          requestKey: null,
        })

      if (allBookingsCheck) {
        allBookingsCheck.forEach(booking => {
          if (booking?.expand?.docket?.status != "Paid" && booking?.expand?.docket?.status != "Closed" && eventDocketID.value == '') {
            eventDocketID.value = booking?.docket || ''
            docketId.value = eventDocketID.value
          }
        })
      }
    } catch(e){
      console.error(e)
      try {
        const eventCheck = await pb.collection("events").getOne(props?.eventId, {expand: 'bookings,bookings.docketLine'})
        eventDocketID.value = eventCheck?.expand?.bookings?.length > 0 && eventCheck?.expand?.bookings[0]?.expand?.docketLine?.docket ? eventCheck?.expand?.bookings[0]?.expand?.docketLine?.docket : ''
      } catch(e){
        console.error(e)
      }
    }
  }
}

const handlePayFromPos = () => {
  if (props?.type === "event") {
    // Always reconcile first — a cached docketId may point at a docket created
    // before the event was last edited, so the recalculated due wouldn't be reflected.
    checkBookingBalance('pos')
    return
  }
  if(docketId.value) {
    goToPos(docketId.value)
  } else if(props?.bookingProp?.docketId) {
    goToPos(props?.bookingProp?.docketId)
  } else if(props?.bookingProp?.expand?.docketLine?.docket) {
    goToPos(props?.bookingProp?.expand?.docketLine?.docket)
  } else if(eventDocketID.value == '') {
    checkBookingBalance('pos')
  }else if(eventDocketID.value !== '') {
    goToPos(eventDocketID.value)
  }
}

onMounted(async ()=>{
  usersForCouponsList.value = [];
  localStorage.setItem('onpayment','1')
  await getUserList(true)
  if(props?.userCreditCards && props.userCreditCards.length > 0){
    selectedCreditCard.value = props.userCreditCards[0].token
  }
  await getCouponList(props?.owner)
  await refreshGiftVoucherAvailability()
  await getDocketsInUse()
  await MaxCouponsToUse()
  doBarcodeReaderSubscribe()

  creditAmtTopay.value = props.totalDue

  const pos = await posSetupStore.load();
  canPayAtPos.value = pos ? true : false;
  currentTab.value = props.haveCreatePermission && canPayAtPos.value ? 'pos' : pb?.authStore?.model?.id
    ? currentTab.value : currentLocationDetails?.value?.onlineCreditCardsEnabled
      ? "creditCard" : props.canPayInStore
        ? "payInStore" : currentTab.value

  if(props?.owner) {
    try {
      let hasMembershipBenefits = await pb.collection("users").getOne(props.owner, {expand: 'membership'});
      bookingOwnerUser.value = hasMembershipBenefits
      if (hasMembershipBenefits && hasMembershipBenefits.preventBenefits) {
        showPreventBenefits.value = true
      }
    } catch(e){}
  }

  if(props?.bookingProp?.users?.length > 0){
    let users:any = [];
    let filter = 'id="' + (pb?.authStore?.model?.id || '') + '"'
    couponUser.value = pb?.authStore?.model?.id || ''
    if(props?.haveCreatePermission){
      users = users.concat(props?.bookingProp?.users || [], [props.owner])
      filter = users.map((u:any) => 'id="'+ u +'"').join("||")
      couponUser.value = props?.bookingProp?.users && props.bookingProp.users[0] ? props.bookingProp.users[0] :  props.owner
    }
    let fields = "id,firstName,lastName,username"
    usersForCouponsList.value = await pb.collection("users").getFullList({filter,fields});
  } else if(pb?.authStore?.model?.id) {
    let fields = "id,firstName,lastName,username"
    let filter = 'id="' + (pb?.authStore?.model?.id || '') + '"'
    usersForCouponsList.value = await pb.collection("users").getFullList({filter,fields});
  }

  await totalDueIsNotProducts()
  await getEventDocket()
})

onUnmounted(()=>{
  localStorage.removeItem('onpayment')
  if(barcodeReaderID.value && barcodeReaderID.value != '') {
    pb.collection("barcodeReaders").unsubscribe(barcodeReaderID.value)
    barcodeReaderID.value = '';
  }
})
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12" sm="4" md="3" class="position-relative pt-5">
        <!-- 👉 Tabs -->
        <VTabs
          v-model="currentTab"
          direction="vertical"
          class="v-tabs-pill"
          grow
        >
          <VTab v-for="item in tabs" :key="item.icon" :value="item.value">
            <VIcon start :icon="item.icon" />
            {{ item.label }}
          </VTab>
        </VTabs>
        <!-- Add vertical divider inside the first column -->
        <VDivider vertical class="divider-absolute" />
      </VCol>

      <VCol cols="12" sm="8" md="9">
        <!-- 👉 Windows -->
        <VWindow v-model="currentTab" :touch-less="true">
          <div class="info-button-container">
            <VTooltip
              v-model="isTooltipVisible"
              activator="click"
              location="top"
              content-class="payment_info_tooltip"
            >
              <template #activator="{ props }">
                <IconBtn
                  v-bind="props"
                  icon="mdi-information-outline"
                  @click.stop
                  @touchstart.stop="isTooltipVisible = !isTooltipVisible"
                />
              </template>
              <div class="tooltip-content">
                <span v-if="currentTab === 'coupon'">
                  <strong>Apply Coupon:</strong><br>Select a coupon from the
                  dropdown list and apply it and then the quantity you wish to use.
                </span>
                <span v-else-if="currentTab === 'userCredit'">
                  The total amount will be deducted from your available account
                  balance.
                </span>
                <span v-else-if="currentTab === 'creditCard'">
                  Enter your credit card details to pay directly with your credit
                  card securely online .
                </span>
                <span v-else-if="currentTab === 'payInStore'">
                  Select this option to pay on arrival at our venue.
                </span>
                <span v-else-if="currentTab === 'pos'">
                  You will be redirected to the Point of Sale (POS) screen to
                  complete the payment. Please ensure all details are correct
                  before proceeding.
                </span>
                <span v-else-if="currentTab === 'giftVoucher'">
                  Enter the gift voucher code in the provided field. We will
                  validate the code and apply the corresponding balance to your
                  purchase.
                </span>
                <span v-else-if="currentTab === 'waiveFee'">
                  Please ensure you have a valid reason to Waive the fee!.
                </span>
              </div>
            </VTooltip>
          </div>

          <!-- Coupons  -->
          <VWindowItem
            v-if="props?.type !== 'coupons'"
            value="coupon"
          >
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay with coupons
              </VCardTitle>
              <div v-if="getUsersForCouponsList.length > 1" class="mb-5">
                <VSelect
                  label="Select a player"
                  placeholder="Select a player"
                  clearable
                  :rules="[!couponUser || couponUser == 0 ? 'Select a player ': true]"
                  v-model="couponUser"
                  :items="getUsersForCouponsList">
                </VSelect>
              </div>
              <div v-if="getCoupons.length > 0">
                <VForm ref="refVFormCoupon" v-if="totalDueNotProducts">
                  <VRow>
                    <VCol cols="12">
                      <VSelect
                        v-model="selectedCoupon"
                        :items="getListCoupons"
                        :rules="[requiredValidator]"
                        item-value="id"
                        label="Select a Coupon"
                        clearable
                      >
                        <template #item="{ props: itemProps, item }">
                          <VListItem
                            v-bind="itemProps"
                            :title="item.raw?.id"
                            :subtitle="`${item.raw.qtyRemaining} remaining · Expires ${moment(item.raw?.expiryTime).format('DD/MM/YYYY')}${item.raw.afterHoursOnly ? ' (After Hours)' : ''}`"
                          />
                        </template>
                        <template #selection="{ item }">
                          <span>{{ item?.raw?.id }}</span>
                        </template>
                      </VSelect>
                    </VCol>
                    <VCol cols="12">
                      <VSelect
                        v-model="quantity"
                        :items="getQuantityList"
                        :rules="[requiredValidator]"
                        :disabled="!selectedCoupon"
                        label="Select Quantity"
                      />
                    </VCol>
                  </VRow>
                  <VCardActions v-if="!processingPayment" class="d-flex justify-end mt-2">
                    <VRow class="d-flex justify-end">
                      <VCol
                        class="d-flex flex-column flex-xs-column flex-sm-row justify-end gap-2 px-0 mt-5">
                        <VBtn
                          variant="flat"
                          color="primary"
                          :disabled="!selectedCoupon || !quantity"
                          @click.prevent="checkBookingBalance('Coupon')"
                        >
                          Apply Coupon
                        </VBtn>
                      </VCol>
                    </VRow>
                  </VCardActions>
                  <div v-else>
                    <v-progress-circular indeterminate></v-progress-circular> Processing...
                  </div>
                </VForm>
                <div v-else>
                  <VAlert class="mb-3" v-if="props?.bookingProp?.payMethod && props.bookingProp.payMethod.includes('Coupon')">
                    Coupons have been applied to the booking.
                  </VAlert>
                  <VAlert color="error" v-if="totalDueNotProducts">Coupons can only be used for Bookings.</VAlert>
                </div>
              </div>
              <div v-else>
                <VAlert color="">Account has no coupons for this booking date available</VAlert>
              </div>
            </VCardText>
          </VWindowItem>

          <!-- user Credit -->
          <VWindowItem
            v-if="props?.type !== 'transferCredit'"
            value="userCredit"
          >
            <VCardText class="pt-0 pb-0">
              <h3 class="mb-2">
                Account balance: 
                <span>
                  ${{ ownerUser?.tempBalance }}
                </span>
              </h3>
              <h3 class="mb-2">
                Loyalty balance: 
                <span>
                  ${{ ownerUser?.loyaltyBalance }}
                </span>
              </h3>
              <VCol cols="12" md="6" class="pt-0 mb-3">
                <v-radio-group v-model="accountType" inline>
                  <v-radio label="Use Account" value="account"></v-radio>
                  <v-radio label="Use Loyalty" value="loyalty"></v-radio>
                </v-radio-group>
              </VCol> 
              <VCardTitle class="d-flex justify-left px-0 pt-0 text-capitalize">
                Pay By {{accountType}} Credit
              </VCardTitle>
              <div class="mb-2">
                Make the payment from your {{ accountType }} balance.
              </div>
              <VForm ref="refCreditAmtForm">
                <VRow>
                  <VCol cols="12" md="6">
                    <VTextField
                      v-model="creditAmtTopay"
                      :rules="[
                        requiredValidator,
                        creditLimitValidator(
                          creditAmtTopay,
                          creditLimitMax,
                          props.totalDue,
                        ),
                      ]"
                      label="Enter Amount"
                      placeholder="60"
                      type="number"
                      min="1"
                      :max="props.totalDue"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VAlert color="error" v-if="accountType === 'loyalty' && ownerUser?.loyaltyBalance <= 0 && ownerUser?.preventBenefits && !(pb.authStore?.model?.roles?.length > 0 && !isUserMode)">
                    Loyalty credit not available while on benefits hold, Please clear your account to restore.
                  </VAlert>
                </VRow>
              </VForm>
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  variant="flat"
                  class="mt-8"
                  :disabled="accountType === 'loyalty' && ownerUser?.loyaltyBalance <= 0 && ownerUser?.preventBenefits  && !(pb.authStore?.model?.roles?.length > 0 && !isUserMode)"
                  @click.prevent="checkBookingBalance('UserCredit')"
                >
                  Pay
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- pos payment -->
          <VWindowItem v-if="props?.haveCreatePermission" value="pos">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay from POS screen
              </VCardTitle>
              <div>
                This payment method allows you to use the POS to pay for the booking.
              </div>
              <VCardActions class="flex-column align-end mt-16 gap-3">
                <VBtn
                  width="200"
                  prepend-icon="mdi-network-pos"
                  variant="flat"
                  @click.prevent="handlePayFromPos"
                >
                  Pay from pos
                </VBtn>
                <VBtn
                  width="200"
                  prepend-icon="mdi-cart"
                  variant="flat"
                  @click.prevent="addToDocket(props)"
                >
                  Add To Docket
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- gift voucher payment -->
          <VWindowItem v-if="props?.type !== 'giftVoucher'" value="giftVoucher">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay from Gift Voucher
              </VCardTitle>
              <div class="mb-2">
                Use all or part of a Gift Voucher to pay for this booking.
              </div>

              <!-- gift voucher form -->
              <VForm ref="refVFormGiftVoucher">
                <VRow>
                  <!-- gift voucher balance -->
                  <VCol
                    v-if="!isInvalidCoupon && !isEmpty(appliedGiftVoucherDetails)"
                    class="d-flex justify-center"
                    cols="12"
                  >
                    <div>
                      Balance Remaining: ${{
                        appliedGiftVoucherDetails?.balanceRemaining
                      }}
                    </div>
                  </VCol>

                  <!-- gift voucher id -->
                  <VCol md="6" cols="12">
                    <VTextField
                      v-model="giftVoucherId"
                      :rules="[requiredValidator]"
                      :messages="apps.POS?.isVisible ? 'or scan the code' : ''"
                      label="Enter Gift Voucher Id"
                      @blur="validateGiftVoucher"
                    />
                  </VCol>

                  <!-- gift voucher amount -->
                  <VCol md="6" cols="12">
                    <VTextField
                      v-if="props?.type === 'Booking'"
                      v-model="amountToRedeem"
                      :disabled="isInvalidCoupon || !giftVoucherId"
                      :rules="[
                        requiredValidator,
                        minMaxValidator(
                          amountToRedeem,
                          0.01,
                          Math.min(
                            props?.totalDue,
                            appliedGiftVoucherDetails?.balanceRemaining || 1,
                          ),
                        ),
                      ]"
                      label="Enter Amount"
                      type="number"
                      prefix="$"
                    />
                    <VTextField
                      v-else
                      v-model="amountToRedeem"
                      :rules="[
                        requiredValidator,
                        minValidator(
                          amountToRedeem,
                          props?.requestedPaymentDetails?.amount,
                        ),
                        decimalRule,
                      ]"
                      label="Enter Amount"
                      type="number"
                      prefix="$"
                    />
                  </VCol>
                </VRow>
              </VForm>

              <!-- gift voucher apply button -->
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  :disabled="props?.type !== 'Booking' && (isInvalidCoupon || !giftVoucherId || appliedGiftVoucherDetails?.balanceRemaining < props?.requestedPaymentDetails?.amount)"
                  variant="flat"
                  @click.prevent="checkBookingBalance('GiftVoucher')"
                >
                  Apply gift voucher
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- waive fee -->
          <VWindowItem v-if="props?.haveCreatePermission" value="waiveFee">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Waive Fee
              </VCardTitle>
              <div class="mb-2">Allows you to waive the fee for the booking.</div>
              <VForm ref="refVFormWaive" class="mt-4">
                <VRow>
                  <VCol lg="6" md="8" sm="10" cols="12">
                    <VTextarea
                      v-model="feeWaiveReason"
                      :rules="[
                        requiredValidator,
                        customRegexValidator(
                          feeWaiveReason,
                          '^.{4,}$',
                          'Please enter minimum of four characters',
                        ),
                      ]"
                      rows="2"
                      label="Reason for Waiving the fee"
                      class="waiveFee_textarea"
                    />
                  </VCol>
                </VRow>
              </VForm>
              <VCardActions class="d-flex justify-end px-0 mt-5">
                <VBtn
                  variant="flat"
                  @click.prevent="checkBookingBalance('WaiveFee')"
                >
                  Waive Fee
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- pay in store  -->
          <VWindowItem v-if="props.canPayInStore" value="payInStore">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay in Store
              </VCardTitle>
              <div>Use this option to pay at the venue when you arrive.</div>
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  variant="flat"
                  class="mt-16"
                  @click.prevent="
                    async () => {
                      await confirmBooking();
                      if (props.loggedInUserId || props.isQuickBooking) {
                        emit('closeDialogForPayLater');
                      } else {
                        emit('bookingChange', props?.bookingId);
                        emit('closeDialog', false);
                      }
                    }">
                  Confirm - Pay later
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- credit card  -->
          <VWindowItem value="creditCard">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Credit Card
              </VCardTitle>
              <div v-if="props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)">
                Use this option to pay online with your credit card.
              </div>
              <VForm ref="refVFormCreditCard">
                <!-- user credit cards -->
                <div class="my-4" v-if="props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)">
                  <VSelect
                    @update:menu="onUpdateMenu"
                    v-model="selectedCreditCard"
                    :items="[
                      ...props?.userCreditCards,
                      {
                        id: 'newCard',
                        cardNum: 'New Card',
                        name: '',
                        exp: '',
                        token: 'newCard',
                      },
                    ]"
                    :rules="[requiredValidator]"
                    item-title="cardNum"
                    label="Cards"
                    item-value="token"
                    placeholder="Select a card"
                  >
                    <template v-slot:item="{ props, item }">
                      <VListItem
                        v-bind="props"
                        :prepend-avatar="getCardProviders(item.raw?.provider)"
                        :subtitle="
                          item.raw.name && item.raw.exp
                            ? item.raw.name + ' | exp - ' + item.raw?.exp
                            : ''
                        "
                      />
                    </template>
                  </VSelect>
                </div>
              </VForm>

              <!-- pay now button -->
              <VCardActions
                class="d-flex px-0"
                :class="
                  props?.loggedInUserId
                    ? 'justify-space-between'
                    : 'justify-end'
                "
              >
                <VCheckbox
                  v-if="props?.loggedInUserId && selectedCreditCard == 'newCard'"
                  v-model="saveNewCard"
                  :disabled="selectedCreditCard !== 'newCard'"
                  label="Save Card for future use"
                />
                <VSpacer v-else/>

                <VBtn
                  variant="flat"
                  @click.prevent="checkBookingBalance('CreditCard')"
                >
                  Pay Now
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>
        </VWindow>
        <div
          v-if="
            props?.type &&
            ['event', 'Booking']?.includes(props?.type) &&
            !['payInStore', 'pos', 'waiveFee']?.includes(currentTab)
          "
        >
          <DiscountApply
            :docket="docketId"
            @applyDiscount="handleDiscountCode"
          />
        </div>
      </VCol>
    </VRow>

    <CardProcessingDialog
      :is-add-card-dialog-visible="isProcessingCardDialogVisible"
      :add-card-dialog-message="addCardDialogMessage"
      :total-amount="props?.totalDue"
      :surcharge-amount="getSurchargeAmount"
      @close-dialog="closeProcessingCardDialog"
    />

    <AddToMyTabDialog
      :is-dialog-visible="isAddToMyTabDialogVisible"
      :user="props?.owner ? props?.owner : ''"
      :docketId="docketId"
      @addToMyTab="(val) => handleAddToMyTab(val)"
      @closeAddToMyTabDialog="(val: any) => (isAddToMyTabDialogVisible = val)"
    />

    <PreventBenefitsDialog
      :showPreventBenefits="showPreventBenefits"
      @closeDialog="showPreventBenefits = false"
      @gotToProfile="goToProfile()"
    />
  </div>
</template>

<style scoped lang="scss">
.info-button-container {
  position: absolute;
  z-index: 10;
  inset-block-start: 0;
  inset-inline-end: 0;
}

:global(.payment_info_tooltip) {
  max-inline-size: 250px !important;
}

.divider-absolute {
  position: absolute;
  z-index: 1;
  block-size: 100%;
  inset-block-start: 0;
  inset-inline-end: -1px;
}
</style>
